import { useEffect, useState } from 'react';

import { Modal, useToast } from '../ui/Overlay';
import { Button } from '../ui/Button';
import { Field, Input } from '../ui/Field';
import { AssignmentList } from './AssignmentList';
import { useResource, useMutation } from '../../hooks/useResource';
import { useAuth } from '../../auth/AuthProvider';
import { api } from '../../lib/api';

/**
 * A fleet, and the managers who run it.
 *
 * This is the screen the platform was missing: a manager's entire world is the
 * fleets they hold, and until someone attaches them to one they sign in to an
 * empty dashboard with no way to tell whether that is a permission problem or
 * an empty depot. The endpoint existed from the start; there was simply nowhere
 * to call it from.
 *
 * Only people who already hold the Manager role can be attached — the server
 * refuses anyone else with `not_a_manager`, so the candidate list asks for that
 * role rather than offering everyone and letting the click fail.
 */
export function ManageFleetModal({ open, fleet, onClose, onChanged }) {
  const toast = useToast();
  const { can } = useAuth();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const canAssign = can('fleet:assign');
  const canWrite = can('fleet:write');

  useEffect(() => {
    setName(fleet?.name || '');
    setDescription(fleet?.description || '');
    setConfirmingDelete(false);
  }, [fleet]);

  const assigned = useResource(open && fleet && canAssign ? `/api/fleets/${fleet.id}/managers` : null);
  const candidates = useResource(open && canAssign ? '/api/users' : null, { role: 'MANAGER', limit: 200 });

  const save = useMutation(async () => {
    await api.patch(`/api/fleets/${fleet.id}`, {
      name: name.trim(),
      description: description.trim() || undefined,
    });
  });

  const remove = useMutation(async () => api.delete(`/api/fleets/${fleet.id}`));

  const toggleManager = async (person, attach) => {
    setBusyId(person.id);
    try {
      await api.post(`/api/fleets/${fleet.id}/managers`, { userId: person.id, attach });
      toast.success(
        attach
          ? `${person.full_name} now manages ${fleet.name}.`
          : `${person.full_name} no longer manages ${fleet.name}.`
      );
      assigned.refetch();
      onChanged?.({ silent: true });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const submit = async () => {
    try {
      await save.run();
      toast.success('Fleet updated.');
      onChanged?.();
      onClose();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const doDelete = async () => {
    try {
      await remove.run();
      toast.success(`${fleet.name} deleted.`);
      onChanged?.();
      onClose();
    } catch (err) {
      // The server refuses to delete a fleet that still holds vehicles, and
      // says how many. That message is more useful than anything we could
      // write here, so it is shown as-is.
      toast.error(err.message);
      setConfirmingDelete(false);
    }
  };

  if (!fleet) return null;

  const assignedIds = new Set((assigned.data?.managers || []).map((m) => m.id));
  const managerCount = assignedIds.size;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={fleet.name}
      description={`${fleet.vehicle_count ?? 0} vehicle${Number(fleet.vehicle_count) === 1 ? '' : 's'} · ${managerCount} manager${managerCount === 1 ? '' : 's'} · ${fleet.timezone || ''}`}
      width="lg"
      footer={
        <>
          {canWrite && !confirmingDelete && (
            <Button
              variant="ghost"
              className="mr-auto text-critical-ink"
              onClick={() => setConfirmingDelete(true)}
            >
              Delete fleet
            </Button>
          )}
          {confirmingDelete && (
            <div className="mr-auto flex items-center gap-2">
              <span className="text-[12.5px] text-ink-2">Delete this fleet?</span>
              <Button variant="danger" size="sm" loading={remove.pending} onClick={doDelete}>
                Yes, delete
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirmingDelete(false)}>
                Keep it
              </Button>
            </div>
          )}
          <Button onClick={onClose}>Close</Button>
          {canWrite && (
            <Button
              variant="primary"
              loading={save.pending}
              onClick={submit}
              disabled={!name.trim() || (name === fleet.name && description === (fleet.description || ''))}
            >
              Save changes
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Fleet name" required>
            <Input value={name} onChange={(e) => setName(e.target.value)} disabled={!canWrite} />
          </Field>
          <Field label="Description" hint="Optional — what this depot or contract covers.">
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={!canWrite}
              placeholder="South-west distribution"
            />
          </Field>
        </div>

        {canAssign && (
          <div className="space-y-2">
            <div>
              <p className="text-[13px] font-medium text-ink">Managers</p>
              <p className="text-[12px] text-ink-3">
                A manager sees the vehicles, trips, alerts and people of the fleets listed here — and
                nothing else in the organisation.
              </p>
            </div>

            <AssignmentList
              items={candidates.data?.users || []}
              assignedIds={assignedIds}
              pendingId={busyId}
              onToggle={toggleManager}
              primary={(person) => person.full_name}
              secondary={(person) => person.email}
              searchPlaceholder="Search managers"
              addLabel="Assign"
              removeLabel="Unassign"
              emptyLabel={
                candidates.loading
                  ? 'Loading…'
                  : 'Nobody holds the Manager role yet. Create one from the People tab first.'
              }
            />
          </div>
        )}
      </div>
    </Modal>
  );
}

/**
 * Vehicle-by-vehicle access for a viewer.
 *
 * A viewer's scope is a list of individual vehicles rather than a fleet, so
 * this is the only way their access is ever granted. Requests from the Access
 * queue land in exactly the same table through the same permission check — this
 * is the direct route, that one is the asked-for route.
 */
export function VehicleAccessModal({ open, person, onClose }) {
  const toast = useToast();
  const [busyId, setBusyId] = useState(null);

  const granted = useResource(open && person ? `/api/users/${person.id}/vehicle-access` : null);
  const vehicles = useResource(open ? '/api/vehicles' : null, { limit: 500 });

  const toggle = async (vehicle, grant) => {
    setBusyId(vehicle.id);
    try {
      await api.post(`/api/users/${person.id}/vehicle-access`, { vehicleId: vehicle.id, grant });
      toast.success(
        grant
          ? `${person.full_name} can now see ${vehicle.reg_number}.`
          : `${vehicle.reg_number} removed from ${person.full_name}.`
      );
      granted.refetch();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusyId(null);
    }
  };

  if (!person) return null;

  const grantedIds = new Set((granted.data?.vehicles || []).map((v) => v.id));

  // Only vehicles in the same organisation can ever be granted — the server
  // answers `cross_org` otherwise, and offering them here would be a click that
  // exists only to fail.
  const offerable = (vehicles.data?.vehicles || []).filter(
    (v) => !person.org_id || v.org_id === person.org_id
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Vehicle access · ${person.full_name}`}
      description={
        grantedIds.size
          ? `Sees ${grantedIds.size} vehicle${grantedIds.size === 1 ? '' : 's'}. Changes take effect immediately.`
          : 'Sees no vehicles yet. Grant one and it appears on their dashboard immediately.'
      }
      width="lg"
      footer={<Button onClick={onClose}>Done</Button>}
    >
      <AssignmentList
        items={offerable}
        assignedIds={grantedIds}
        pendingId={busyId}
        onToggle={toggle}
        primary={(v) => v.reg_number}
        secondary={(v) => [v.fleet_name, v.make, v.model].filter(Boolean).join(' · ')}
        searchPlaceholder="Search by registration"
        addLabel="Grant"
        removeLabel="Revoke"
        emptyLabel={vehicles.loading ? 'Loading…' : 'No vehicles available to grant.'}
      />
    </Modal>
  );
}
